/**
 * Coalesces concurrent requests while one async drain is already running.
 *
 * One request received during the first run produces exactly one immediate
 * rerun. Requests received during that rerun are deferred to a microtask, so
 * callers cannot create synchronous recursion or an unbounded tight loop.
 */
export class SingleFlightRerun {
  private inFlight: Promise<void> | null = null;
  private rerunRequested = false;

  waitForIdle(): Promise<void> {
    return this.inFlight ?? Promise.resolve();
  }

  run(work: () => Promise<void>): Promise<void> {
    if (this.inFlight) {
      this.rerunRequested = true;
      return this.inFlight;
    }

    const cycle = async (): Promise<void> => {
      this.rerunRequested = false;
      await work();
      if (this.rerunRequested) {
        this.rerunRequested = false;
        await work();
      }
    };
    const current = cycle().finally(() => {
      if (this.inFlight === current) this.inFlight = null;
      if (!this.rerunRequested) return;

      this.rerunRequested = false;
      queueMicrotask(() => {
        void this.run(work).catch(() => undefined);
      });
    });
    this.inFlight = current;
    return current;
  }
}
